import React, { useState, useEffect } from 'react';
import DataTable from "react-data-table-component";
import { useGlobalContext } from "../GlobalContext";

const ListUser = () => {
  const { api, isReady } = useGlobalContext();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]); // <--- Add state for roles
  const [modalAddOpen, setModalAddOpen] = useState(false);
  const [modalEditOpen, setModalEditOpen] = useState(false);
  const [searchText, setSearchText] = useState("");

  // State untuk form tambah user
  const [newUser, setNewUser] = useState({
    kode: '',
    pengguna: '',
    password: '',
    kode_role: '',
  });

  // State untuk form edit user
  const [editUser, setEditUser] = useState({
    kode: '',
    pengguna: '',
    password: '',
    kode_role: '',
  });

  useEffect(() => {
    if (isReady) {
      getUsers();
      getRoles(); // <--- Fetch roles when the component is ready
    }
  }, [isReady]);

  const getUsers = async () => {
    try {
      const response = await api.get('/api/users');
      setUsers(response.data.data || []);
    } catch (err) {
      console.error("Gagal mengambil data users:", err.response?.status, err.message);
      setUsers([]);
    }
  };

  // <--- Add getRoles function to ListUser
  const getRoles = async () => {
    try {
      const response = await api.get('/api/setup/role'); // This is the endpoint from ListRole      
      setRoles(response.data || []);
    } catch (error) {
      console.error("Gagal mengambil data role:", error);
      if (error.response && error.response.status === 401) {
        alert("Akses ditolak. Silakan login kembali.");
      }
      setRoles([]); // Ensure roles is an empty array on error
    }
  };

  const handleChange = (e, isEdit = false) => {
    const { name, value } = e.target;
    if (isEdit) {
      setEditUser((prev) => ({ ...prev, [name]: value }));
    } else {
      setNewUser((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleAddUser = async () => {
    try {
      await api.post('/api/users', newUser);      
      setModalAddOpen(false);
      getUsers();
      // Optionally, reset new user form
      setNewUser({
        kode: '',
        pengguna: '',
        password: '',
        kode_role: '',
      });
    } catch (err) {
      console.error("Gagal menambah user:", err.response?.data?.message || err.message);
      alert('Gagal menambah user: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleEditUser = async () => {
    try {
      await api.patch(`/api/users/${editUser.kode}`, editUser);
      setModalEditOpen(false);
      getUsers();
    } catch (err) {
      console.error("Gagal mengedit user:", err.response?.data?.message || err.message);
      console.log("Edit user data sent:", editUser); // Debugging
      alert('Gagal mengedit user: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleDelete = async (kode) => {
    if (window.confirm('Yakin hapus user ini?')) {
      try {
        await api.delete(`/api/users/${kode}`);
        getUsers();
      } catch (err) {
        console.error("Gagal menghapus user:", err.response?.data?.message || err.message);
        alert('Gagal menghapus user: ' + (err.response?.data?.message || err.message));
      }
    }
  };

  const handleEdit = (user) => {
    setEditUser({
      kode: user.kode,
      pengguna: user.pengguna,
      password: '', // Clear password for security
      kode_role: user.kode_role || '', // Ensure kode_role is set from existing user data
    });
    setModalEditOpen(true);
  };

  const openAddModal = () => {
    setNewUser({
      kode: '',
      pengguna: '',
      password: '',
      kode_role: '', // Reset kode_role as well
    });
    setModalAddOpen(true);
  };

  // Utility tampilan level (kapital huruf pertama)
  const displayLevel = (lev) => {
    if (!lev) return '-';
    // Find the role in the `roles` state to get its `keterangan`
    const foundRole = roles.find(role => role.kode_role === lev);
    return foundRole ? foundRole.keterangan : lev; // Display keterangan if found, otherwise the kode_role
  };

  // Define columns for react-data-table-component
  const columns = [
    {
      name: "No",
      selector: (row, index) => index + 1,
      width: "60px",
      center: true,
    },
    {
      name: "Kode",
      selector: (row) => row.kode,
      sortable: true,
      center: true,
      width: "100px",
    },
    {
      name: "Pengguna",
      selector: (row) => row.pengguna,
      sortable: true,
    },
    {
      name: "Level",
      // Modified to display the actual role description based on `kode_role`
      selector: (row) => displayLevel(row.kode_role),
      width: "120px",
    },
    {
      name: "Aksi",
      cell: (row) => (
        <div className="d-flex gap-2 justify-content-center">
          <button className="btn btn-info btn-sm" onClick={() => handleEdit(row)}>
            <i className="fa fa-edit" />
          </button>
          <button className="btn btn-danger btn-sm" onClick={() => handleDelete(row.kode)}>
            <i className="fa fa-trash" />
          </button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "150px",
      center: true,
    },
  ];

  // Filtered data for search functionality
  const filteredUsers = users.filter(
    (user) =>
      user.kode.toLowerCase().includes(searchText.toLowerCase()) ||
      user.pengguna.toLowerCase().includes(searchText.toLowerCase()) ||
      (displayLevel(user.kode_role) && displayLevel(user.kode_role).toLowerCase().includes(searchText.toLowerCase())) // Search by displayed level
  );

  // Custom styles for the DataTable
  const customStyles = {
    headCells: {
      style: {
        fontWeight: 'bold',
        fontSize: '16px',
        backgroundColor: '#f0f0f0',
        padding: '10px 12px',
        borderRight: '2px solid #ddd',
      },
    },
    cells: {
      style: {
        borderRight: '1px solid #ddd',
        padding: '12px 10px',
      },
    },
    rows: {
      style: {
        borderBottom: '1px solid #eee',
      },
    },
  };

  return (
    <div className="card shadow-sm">
      <div className="card-header">
        <div className="d-flex justify-content-end align-items-center w-100">
          <h4 className="mb-0" style={{ fontSize: "22px" }}>DAFTAR USER</h4>
         <button
            className="btn btn-primary"
            style={{ fontSize: "14px", padding: "6px 14px" }}
            onClick={openAddModal}
          >
          Tambah Data
         </button>
        </div>
      </div>

      <div className="card-body">
        <div className="mb-2 d-flex justify-content-end">
          <input
            type="text"
            className="form-control form-control-sm"
            style={{ maxWidth: "200px" }}
            placeholder="Cari kode/pengguna/level..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>
        <DataTable
          columns={columns}
          data={filteredUsers}
          pagination
          striped
          dense
          highlightOnHover
          responsive
          customStyles={customStyles}
          className="table table-bordered"
        />
      </div>

      {/* Modal Tambah User */}
      {modalAddOpen && (
        <div className="modal show d-block" tabIndex="-1" role="dialog">
          <div className="modal-dialog modal-md" role="document">
            <div className="modal-content shadow">
              <div className="modal-header">
                <h5 className="modal-title">TAMBAH USER</h5>
                <button type="button" className="btn-close" onClick={() => setModalAddOpen(false)}></button>
              </div>
              <div className="modal-body">
                <div className="form-group mb-2">
                  <label>KODE</label>
                  <input
                    type="text"
                    className="form-control"
                    name="kode"
                    value={newUser.kode}
                    onChange={handleChange}
                    required
                    autoComplete="off"
                  />
                </div>
                <div className="form-group mb-2">
                  <label>PENGGUNA</label>
                  <input
                    type="text"
                    className="form-control"
                    name="pengguna"
                    value={newUser.pengguna}
                    onChange={handleChange}
                    required
                    autoComplete="off"
                  />
                </div>
                <div className="form-group mb-2">
                  <label>PASSWORD</label>
                  <input
                    type="password"
                    className="form-control"
                    name="password"
                    value={newUser.password}
                    onChange={handleChange}
                    required
                    autoComplete="new-password"
                  />
                </div>
                <div className="form-group mb-2">
                  <label>LEVEL</label>
                  <select
                    className="form-control"
                    name="kode_role"
                    value={newUser.kode_role}
                    onChange={handleChange}
                    required
                    autoComplete="off"
                  >
                    <option value="">-- Pilih Level --</option>
                    {roles
                    .filter((role) => role.status === "true")
                    .map((role) => (
                      <option key={role.kode_role} value={role.kode_role}>
                        {role.keterangan}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary btn-sm" onClick={() => setModalAddOpen(false)}>Batal</button>
                <button className="btn btn-success btn-sm" onClick={handleAddUser}>Simpan</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Edit User*/}
      {modalEditOpen && (
        <div className="modal show d-block" tabIndex="-1" role="dialog">
          <div className="modal-dialog modal-md" role="document">
            <div className="modal-content shadow">
              <div className="modal-header">
                <h5 className="modal-title">EDIT USER</h5>
                <button type="button" className="btn-close" onClick={() => setModalEditOpen(false)}></button>
              </div>
              <div className="modal-body">
                <div className="form-group mb-2">
                  <label>KODE</label>
                  <input
                    disabled
                    className="form-control"
                    name="kode"
                    value={editUser.kode}
                  />
                </div>
                <div className="form-group mb-2">
                  <label>PENGGUNA</label>
                  <input
                    className="form-control"
                    name="pengguna"
                    value={editUser.pengguna}
                    onChange={(e) => handleChange(e, true)}
                    autoComplete="off"
                  />
                </div>
                <div className="form-group mb-2">
                  <label>PASSWORD</label>
                  <input
                    type="password"
                    className="form-control"
                    name="password"
                    value={editUser.password}
                    onChange={(e) => handleChange(e, true)}
                    placeholder="Biarkan kosong jika tidak ingin mengubah"
                    autoComplete="new-password"
                  />
                </div>
                <div className="form-group mb-2">
                  <label>LEVEL</label>
                  <select
                    className="form-control"
                    name="kode_role"
                    value={editUser.kode_role}
                    onChange={(e) => handleChange(e, true)}
                    autoComplete="off"
                  >
                    <option value="">-- Pilih Level --</option>
                    {roles
                    .filter((role) => role.status === "true")
                    .map((role) => (
                      <option key={role.kode_role} value={role.kode_role}>
                        {role.keterangan}
                      </option>
                    ))}                  
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary btn-sm" onClick={() => setModalEditOpen(false)}>Batal</button>
                <button className="btn btn-success btn-sm" onClick={handleEditUser}>Simpan</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListUser;